import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Flex } from 'ui/acro'
import Text from 'ui/acro/typography/Text'
import OTPInput from 'react-otp-input'
import IncorrectOtpError from './IncorrectOtpError'
import { axios } from '../../../api'
import AloMovesAnimation from 'components_v2/Search/common/AloMovesAnimation'
import {
  CALLBACK_URL,
  setCookie,
  redirect_to_with_cookie_creation
} from 'components_v2/common'
import { sendAccessTokenToApp, createPasteHandler } from './utils'
import StyledLink from 'components_v2/CommonComponents/StyledLink'
import { verify_account_event } from 'gtm/pushEvents'
import { verify_account_braze_event } from 'braze/brazeEvents'

const Container = styled(Flex)({
  flexDirection: 'column',
  gap: '16px',
  padding: '48px',
  '@media (max-width: 640px)': {
    padding: '48px 20px 24px'
  }
})
const TitleText = styled(Flex)({
  color: '#000',
  fontFamily: 'Proxima-Nova',
  fontSize: '24px',
  fontWeight: 600,
  lineHeight: '32px',
  textTransform: 'uppercase',
  justifyContent: 'center'
})

const DescriptionText = styled(Text)({
  color: '#3B3B3B',
  fontFamily: 'Proxima-Nova',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  letterSpacing: '0.352px'
})

const BottomLinks = styled(Flex)({
  flexDirection: 'column',
  marginTop: '8px'
})

const ForgotPasswordStep2 = ({
  email,
  setStep,
  setCustomerEmail,
  fromPage,
  resending,
  resetPassword = () => {},
  iosAppQueryParams,
  setError,
  isBlocked,
  setIsBlocked
}) => {
  const [otp, setOtp] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showOtpError, setShowOtpError] = useState(false)
  const [erroMessage, setErroMessage] = useState(
    'Incorrect Code. Please try again.'
  )
  const isFromIOSApp = Boolean(iosAppQueryParams?.client_id)
  const [blockedUntil, setBlockedUntil] = useState(null)
  const [disableSendAgainLink, setDisableSendAgainLink] = useState(false)

  useEffect(() => {
    const otpBlockedUntil = localStorage.getItem('otp_blocked_until')?.[email]

    if (otpBlockedUntil) {
      const time = parseInt(otpBlockedUntil, 10)

      if (Date.now() < time) {
        setBlockedUntil(time)
        setIsBlocked(true)
      } else {
        clearLocalStorage()
      }
    }
  }, [])

  useEffect(() => {
    if (!blockedUntil) return

    const remainingTime = blockedUntil - Date.now()

    if (remainingTime <= 0) {
      setIsBlocked(false)
      setBlockedUntil(null)
      clearLocalStorage()
      return
    }

    const timeout = setTimeout(() => {
      setIsBlocked(false)
      setBlockedUntil(null)
      clearLocalStorage()
    }, remainingTime)

    return () => clearTimeout(timeout)
  }, [blockedUntil])

  const clearLocalStorage = () => {
    const data = JSON.parse(localStorage.getItem('otp_blocked_until')) || {}
    delete data[email]

    localStorage.setItem('otp_blocked_until', JSON.stringify(data))
  }

  const handleChange = (value) => {
    setOtp(value)
    if (value?.length === 6) {
      VerifyOtp(value)
    }
  }

  const handlePaste = createPasteHandler({
    onPasteComplete: handleChange
  })

  const VerifyOtp = async (otpEntered) => {
    setIsSubmitting(true)
    let cancelIsSubmitting = false
    try {
      const res = await axios.post(
        fromPage === 'verify'
          ? '/sso/shopify_login_with_otp'
          : '/sso/login_with_otp',
        {
          otp: otpEntered,
          email,
          ...iosAppQueryParams,
          redirect_to: CALLBACK_URL
        }
      )

      const {
        login = 'failed',
        existingUser = false,
        showConcernToLink = false,
        redirectUrl = '/',
        error = '',
        linkedUser,
        user,
        accessTokens,
        tier = '',
        isAlreadyLinked = false
      } = res.data

      if (login === 'successful') {
        if (fromPage === 'verify') {
          localStorage.setItem('signupSuccess', 'true')
        }
        // Fire the account_linked event from Web. For iOS, fire the event from the iOS codebase.
        if (!isFromIOSApp && !isAlreadyLinked) {
          verify_account_event(existingUser, tier, user)
          verify_account_braze_event(existingUser, tier, user)
        }
        setCookie('alo_access_session_established', 'true', 1 / 24)
        if (existingUser) {
          if (showConcernToLink) {
            const otpRes = await axios.post('/sso/send_alo_link_otp', {
              email,
              ...iosAppQueryParams
            })
            const { message } = otpRes.data

            if (message === 'OTP sent successfully') {
              cancelIsSubmitting = true
              setCookie('alo_access_session_established', 'false', 1 / 24)
              setCustomerEmail(email)
              setError(false)
            } else if (
              message === 'Too many requests. Please try again later.'
            ) {
              setError(message)
            }
          } else {
            sendAccessTokenToApp({
              user,
              email,
              accessTokens,
              linkedUser,
              method: 'otp',
              isVerifyWebViewSuccess: !isAlreadyLinked,
              isExistingAwcUser: existingUser
            })
            if (!isFromIOSApp) {
              // First link: home so welcome popup shows before survey
              redirect_to_with_cookie_creation(
                !isAlreadyLinked ? '/' : redirectUrl || '/'
              )
            }
          }
        } else {
          sendAccessTokenToApp({
            user,
            email,
            accessTokens,
            linkedUser,
            method: 'otp',
            isVerifyWebViewSuccess: !isAlreadyLinked,
            isExistingAwcUser: existingUser
          })
          if (!isFromIOSApp) {
            // First link: home so welcome popup shows before survey
            redirect_to_with_cookie_creation('/')
          }
        }
      } else {
        if (error) {
          setErroMessage(error || 'Incorrect Code. Please try again.')
          setShowOtpError(true)
          sendAccessTokenToApp({ email, error, isVerifyWebViewSuccess: false })
        }
      }
    } catch (errorval) {
      setErroMessage(
        errorval?.response?.data?.error ||
          errorval?.response?.data?.message ||
          'Incorrect Code. Please try again.'
      )
      setShowOtpError(true)
      if (
        errorval?.response?.status === 401 &&
        errorval?.response?.data?.error ===
          'Maximum attempts exceeded. Please request a new OTP.'
      ) {
        setOtp('')
        setIsBlocked(true)
      }
      if (errorval?.response?.status === 403) {
        setOtp('')
        const blockTime = Date.now() + 15 * 60 * 1000 // 15 minutes
        setBlockedUntil(blockTime)
        setIsBlocked(true)

        localStorage.setItem(
          'otp_blocked_until',
          JSON.stringify({ [email]: blockTime })
        )
      }
    } finally {
      if (!cancelIsSubmitting) {
        setIsSubmitting(false)
      }
    }
  }

  const handleSendAgain = () => {
    setDisableSendAgainLink(true)

    setTimeout(() => {
      setDisableSendAgainLink(false)
    }, 60000)

    resetPassword()
  }

  return isSubmitting ? (
    <Container>
      <AloMovesAnimation />
    </Container>
  ) : (
    <Container>
      <TitleText>Enter Code</TitleText>
      <DescriptionText>
        Please enter the 6-digit code sent to {email}
      </DescriptionText>
      <OTPInput
        value={otp}
        onChange={handleChange}
        numInputs={6}
        isInputNum={true}
        inputType='tel'
        inputMode='numeric'
        containerStyle={{ justifyContent: 'center' }}
        renderInput={(props) => (
          <input
            {...props}
            disabled={isBlocked}
            onPaste={isBlocked ? (e) => e.preventDefault() : handlePaste}
            style={{
              width: '32px',
              height: '44px',
              fontSize: '18px',
              marginLeft: '15px',
              marginRight: '12px',
              textAlign: 'center',
              borderRadius: '4px',
              padding: '0px',
              border: '1px solid #ccc',
              backgroundColor: isBlocked ? '#eee' : 'white',
              cursor: isBlocked ? 'not-allowed' : 'text'
            }}
          />
        )}
      />
      {/* <VerifyBtn
        type='submit'
        width='100%'
        mt='32px'
        disabled={otp.length < 5}
        processing={isSubmitting}
        id='alo_otp_verify_button'
        onClick={() => VerifyOtp()}
      >
        Verify
      </VerifyBtn> */}
      {showOtpError && (
        <IncorrectOtpError
          message={erroMessage}
          show={showOtpError}
          onClose={() => setShowOtpError(false)}
        />
      )}
      <BottomLinks>
        <StyledLink style={{ marginBottom: '12px' }}>
          Didn’t get an email?
          <span
            onClick={
              blockedUntil || disableSendAgainLink ? undefined : handleSendAgain
            }
            style={{
              cursor:
                blockedUntil || disableSendAgainLink
                  ? 'not-allowed'
                  : 'pointer',
              color: blockedUntil || disableSendAgainLink ? '#aaa' : '',
              pointerEvents:
                blockedUntil || disableSendAgainLink ? 'none' : 'auto'
            }}
          >
            {resending ? 'Sending...' : 'Send again'}
          </span>
        </StyledLink>
        <StyledLink>
          <span
            style={{ marginLeft: 0 }}
            onClick={() => {
              setStep(1)
            }}
          >
            Go Back
          </span>
        </StyledLink>
      </BottomLinks>
    </Container>
  )
}

export default ForgotPasswordStep2
